import { Paragraph } from '@purpur/library';
import { purpurBreakpointLg, purpurBreakpointMd } from '@purpur/library/tokens';
import styled, { css } from 'styled-components';
import { match } from 'ts-pattern';

import { getPurpurColor, getPurpurSpacing, purpurMedia } from '@telialt/common-theme/src/purpurSelectors';

import { BlockSection } from '../common/Common.styles';
import { ContentfulImage } from '../common/ContentfulImage';
import { ResponsiveImage } from '../common/ResponsiveImage';
import { BlockPromoBackground, BlockPromoDisplayVariant } from './model';

type BackgroundVariantProps = {
    $backgroundVariant?: BlockPromoBackground;
};

export const BlockPromoSection = styled(BlockSection)<BackgroundVariantProps>`
    background: ${({ $backgroundVariant }) =>
        match($backgroundVariant)
            .with('light', () => getPurpurColor('backgroundToneOnToneSecondary'))
            .with('dark', () => getPurpurColor('backgroundToneOnTonePrimary'))
            .with('black', () => getPurpurColor('backgroundStatusBlackFriday'))
            .otherwise(() => 'none')};
`;

export const BlockPromoContainer = styled.div<BackgroundVariantProps>``;

type CardsContainerProps = {
    $displayVariant?: BlockPromoDisplayVariant;
};

export const BlockPromoCardsContainer = styled.ul<CardsContainerProps>`
    list-style: none;
    margin: 0;
    padding: 0;

    @container promotion-card (min-width: 600px) {
        [class*='_purpur-promotion-card__image-wrapper--decorative'] picture {
            height: 100%;
        }

        [class*='_purpur-promotion-card--with-image_'] {
            grid-template-rows: 1fr;
        }
    }

    ${({ $displayVariant }) =>
        match($displayVariant)
            .with(
                '1-1-landscape',
                () => css`
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: ${getPurpurSpacing('300')};
                `,
            )
            .with(
                '1-1-portrait',
                () => css`
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: ${getPurpurSpacing('300')};

                    ${purpurMedia('md')`
                        grid-template-columns: repeat(2, 1fr);
                    `};
                `,
            )
            .with(
                '1-1-1',
                () => css`
                    display: none;

                    ${purpurMedia('lg')`
                        display: grid;
                        grid-template-columns: repeat(3, 1fr);
                        gap: ${getPurpurSpacing('300')};
                    `};
                `,
            )
            .with(
                '1-2',
                () => css`
                    display: none;

                    ${purpurMedia('lg')`
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                        gap: ${getPurpurSpacing('300')};
                        > li:first-child {
                            grid-column: span 2;
                        }
                    `};
                `,
            )
            .with(
                '2-2',
                () => css`
                    display: none;

                    ${purpurMedia('lg')`
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                        gap: ${getPurpurSpacing('300')};
                    `};
                `,
            )
            .with(
                '1-3',
                () => css`
                    display: none;

                    ${purpurMedia('lg')`
                        display: grid;
                        grid-template-columns: repeat(3, 1fr);
                        gap: ${getPurpurSpacing('300')};
                        > li:first-child {
                            grid-column: span 3;
                        }
                    `};
                `,
            )
            .otherwise(
                () => css`
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: ${getPurpurSpacing('800')};

                    ${purpurMedia('md')`
                        gap: ${getPurpurSpacing('600')};
                    `};
                `,
            )};
`;

export const BlockPromoMobileCarouselContainer = styled.div`
    ${purpurMedia('lg')`
        display: none;
    `};
`;

export const PromoCardContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${getPurpurSpacing('150')};
`;

export const DescriptionList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
`;

export const DescriptionListItemParagraph = styled(Paragraph)`
    display: flex;
    gap: ${getPurpurSpacing('100')};

    [class^='_purpur-icon_'] {
        flex-shrink: 0;
        margin-top: 0.2rem;
    }
`;

export const StyledResponsiveImage = styled(ResponsiveImage)`
    max-width: 100%;
    max-height: 100%;
    overflow: hidden;
`;

export const ImageCtasContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${getPurpurSpacing('200')};
    padding-top: ${getPurpurSpacing('400')};
`;

// Increasing specificity with double selectors to override styles coming from Purpur
export const PromoCardSmallImage = styled(ContentfulImage)`
    max-width: 100%;
    max-height: 100%;

    && {
        display: block;
        @container promotion-card (min-width: ${purpurBreakpointMd}) {
            display: none !important;
        }
    }
`;

export const PromoCardMediumImage = styled(ContentfulImage)`
    max-width: 100%;
    max-height: 100%;

    && {
        display: none;
        @container promotion-card (min-width: ${purpurBreakpointMd}) {
            display: block;
        }
        @container promotion-card (min-width: ${purpurBreakpointLg}) {
            display: none;
        }
    }
`;

export const PromoCardLargeImage = styled(ContentfulImage)`
    max-width: 100%;
    max-height: 100%;

    && {
        display: none;
        @container promotion-card (min-width: ${purpurBreakpointLg}) {
            display: block;
        }
    }
`;

export const Slot = styled.div`
    margin-top: ${getPurpurSpacing('400')};
`;

export const PromoCardBadgeWrapper = styled.div`
    display: flex;
    padding-bottom: ${getPurpurSpacing('200')};
`;
